import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  getApiErrorDetail,
  UNIQUENESS_CHECK_DEBOUNCE_MS,
  useApiClient,
  useDebouncedValue,
} from '@klareospace/pharos-lib-front-utils';

import type { CreateSpaceFormInput } from '../schemas/create-space.schema';
import { createSpaceApi, spaceKeys } from '../api';
import { useCreateSpace } from './use-create-space';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { CreateSpaceFormInput };

export interface UseCreateSpaceFormReturn {
  form: UseFormReturn<CreateSpaceFormInput>;
  isPending: boolean;
  onSubmit: (data: CreateSpaceFormInput) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCreateSpaceForm(onSuccess: () => void): UseCreateSpaceFormReturn {
  const { t } = useTranslation('settings');
  const createSpace = useCreateSpace();
  const client = useApiClient('admin-auth');
  const api = createSpaceApi(client);

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().trim().min(1, t('spaces.create.validation.codeRequired')),
        description: z.string().trim(),
        name: z.string().trim().min(1, t('spaces.create.validation.nameRequired')),
      }),
    [t],
  );

  const form = useForm<CreateSpaceFormInput>({
    defaultValues: {
      code: '',
      description: '',
      name: '',
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // ─── Debounced code uniqueness check ──────────────────────────────────────

  const codeValue = form.watch('code');
  const codeError = form.formState.errors.code;
  const debouncedCode = useDebouncedValue(codeValue, UNIQUENESS_CHECK_DEBOUNCE_MS);

  const { data: codeCheckData } = useQuery({
    enabled: !!debouncedCode && debouncedCode.length >= 1 && codeError?.type !== 'manual',
    gcTime: 60_000,
    queryFn: () => api.checkCodeExists(debouncedCode),
    queryKey: spaceKeys.checkCode(debouncedCode),
    staleTime: 30_000,
  });

  const { setError } = form;
  useEffect(() => {
    if (codeCheckData === true && debouncedCode && codeValue) {
      setError('code', { message: t('spaces.create.validation.codeExists'), type: 'manual' });
    }
  }, [codeCheckData, codeValue, debouncedCode, setError, t]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  function onSubmit(data: CreateSpaceFormInput): void {
    if (createSpace.isPending) return;

    createSpace
      .mutateAsync({
        code: data.code,
        description: data.description || undefined,
        name: data.name,
      })
      .then(
        () => {
          toast.success(t('spaces.create.success'));
          onSuccess();
        },
        (error: unknown) => {
          const detail = getApiErrorDetail(error);
          if (detail.statusCode === 409) {
            const message = detail.message ?? '';
            if (message.includes('code')) {
              form.setError('code', { message: t('spaces.create.validation.codeExists') });
            } else {
              form.setError('name', { message: t('spaces.create.validation.nameExists') });
            }
            return;
          }
          toast.error(detail.message || t('spaces.create.error'));
        },
      );
  }

  return {
    form,
    isPending: createSpace.isPending,
    onSubmit,
  };
}
